import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CssBaseline,
  useMediaQuery,
  useTheme,
  Box,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ListAltIcon from "@mui/icons-material/ListAlt";
import InfoIcon from "@mui/icons-material/Info";
import SyncLockIcon from "@mui/icons-material/SyncLock";
import LogoutIcon from "@mui/icons-material/Logout";
import ArrowForwardSharpIcon from "@mui/icons-material/ArrowForwardSharp";
import ArrowBackSharpIcon from "@mui/icons-material/ArrowBackSharp";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useTranslation } from "react-i18next";
import { setLanguage } from "../../redux/slices/languageSlice";
import { logout } from "../../features/auth/slices/authSlice";
import logo from "../../features/auth/assets/logo-login.svg";
import { Outlet, useNavigate } from "react-router-dom";
import clientRoutes from "../../constants/clientRoutes";
import { useCallback, useMemo, useState } from "react";
import { AuthorizeHelper } from "../auth";
import Roles from "../../constants/roles";
import adminRoutes from "../../constants/adminRoutes";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import salesRepresentativeRoutes from "../../constants/salesRepresentativeRoutes";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PeopleIcon from '@mui/icons-material/People';
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { CustomSnackbar } from "../shared";

const drawerWidth = 275;

const CustomLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const direction = useAppSelector((state) => state.languageSlice.direction);
  const { businessId } = useAppSelector((state) => state.authSlice);
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);

  const [selectedItem, setSelectedItem] = useState<number | null>(0);
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    dispatch(setLanguage(lng));
  };

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prevState) => !prevState);
  }, []);

  const handleLogout = useCallback(() => {
    dispatch(logout());
    navigate("/login");
  }, [dispatch, navigate]);

  const handleListItemClick = useCallback(
    (index: number, route: string) => {
      if (route === "/logout") {
        handleLogout();
      } else {
        setSelectedItem(index);
        navigate(route);
      }
    },
    [navigate, handleLogout]
  );

  const drawerItems = useMemo(
    () => [
      {
        text: t("sideBar.customerInfo"),
        icon: <InfoIcon />,
        route: clientRoutes.INDEX,

        authorizedRoles: [Roles.CLIENT],
      },
      {
        text: t("sideBar.orders"),
        icon: <ListAltIcon />,
        route: clientRoutes.ORDERS,

        authorizedRoles: [Roles.CLIENT],
      },
      {
        text: t("sideBar.preOrders"),
        icon: <VerifiedUserOutlinedIcon />,
        route: adminRoutes.PRE_ORDERS,

        authorizedRoles: [Roles.ADMIN],
      },
      {
        text: t("sideBar.representantSites"),
        icon: <StorefrontOutlinedIcon />,
        route: adminRoutes.REPRESENTANT_SITES,

        authorizedRoles: [Roles.ADMIN],
      },
      // {
      //   text: t("sideBar.objectives"),
      //   icon: <EmojiEventsOutlinedIcon />,
      //   route: adminRoutes.OBJECTIVES,

      //   authorizedRoles: [Roles.ADMIN],
      // },
      {
        text: t("sideBar.changePassword"),
        icon: <SyncLockIcon />,
        route: clientRoutes.CHANGE_PASSWORD,

        authorizedRoles: [Roles.CLIENT],
      },
      {
        text: t("sideBar.newPreOrder"),
        icon: <AddCircleOutlineOutlinedIcon />,
        route: salesRepresentativeRoutes.NEW_PREORDER,

        authorizedRoles: [Roles.REPRESENTATIVE],
      },
  
      {
        text: t("sideBar.ordersPre"),
        icon: <ListAltIcon />,
        route: salesRepresentativeRoutes.LIST_PRE_COMMANDES,
        authorizedRoles: [Roles.REPRESENTATIVE],
      },
      {
        text: t("sideBar.customers"),
        icon: <PeopleIcon />,
        route: salesRepresentativeRoutes.LIST_CLIENTS,
        authorizedRoles: [Roles.REPRESENTATIVE],
      },
      {
        text: t("sideBar.orders"),
        icon: <ListAltIcon />,
        route: salesRepresentativeRoutes.LIST_COMMANDES,
        authorizedRoles: [Roles.REPRESENTATIVE],
      },
      {
        text: t("sideBar.changePassword"),
        icon: <SyncLockIcon />,
        route: clientRoutes.CHANGE_PASSWORD,

        authorizedRoles: [Roles.REPRESENTATIVE],
      },
      {
        text: t("sideBar.changePassword"),
        icon: <SyncLockIcon />,
        route: clientRoutes.CHANGE_PASSWORD,

        authorizedRoles: [Roles.ADMIN],
      },
      { text: t("sideBar.logout"), icon: <LogoutIcon />, route: "/logout" },
    ],
    [t]
  );

  const filteredDrawerItems = drawerItems.filter(
    (item) =>
      !item.authorizedRoles ||
      AuthorizeHelper.IsAuthorizedRole(item.authorizedRoles)
  );

  const drawer = useMemo(
    () => (
      <div style={{ paddingTop: "57px" }}>
        <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
          <img
            src={logo}
            alt="Logo"
            style={{ maxWidth: "80%", height: "auto" }}
          />
        </Box>
        <List>
          {filteredDrawerItems.map((item, index) => (
            <ListItem
              key={index}
              onClick={() => handleListItemClick(index, item.route)}
              sx={{
                backgroundColor: selectedItem === index ? "#C2975B" : "inherit",
                color: selectedItem === index ? "#fff" : "inherit",
                borderRadius: "5px",
                flexDirection: direction === "rtl" ? "row-reverse" : "initial",
                cursor: "pointer",
              }}
            >
              <ListItemIcon
                sx={{
                  color: selectedItem === index ? "#fff" : "inherit",
                  minWidth: "34px",
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{
                  textAlign: direction === "rtl" ? "end" : "inherit",
                  marginRight: direction === "rtl" ? "10px" : "0px",
                }}
              />
              {selectedItem === index &&
                (direction === "rtl" ? (
                  <ArrowBackSharpIcon />
                ) : (
                  <ArrowForwardSharpIcon />
                ))}
            </ListItem>
          ))}
        </List>
      </div>
    ),
    [filteredDrawerItems, selectedItem, direction, handleListItemClick]
  );

  const adjustedDrawerWidth =
    direction === "rtl" ? drawerWidth + 20 : drawerWidth;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: direction === "rtl" ? "row-reverse" : "row",
      }}
    >
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar
          sx={{ flexDirection: direction === "rtl" ? "row-reverse" : "row" }}
        >
          {isMobileOrTablet && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ marginRight: theme.spacing(2) }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            variant="h6"
            noWrap
            sx={{ marginRight: direction === "rtl" ? "3%" : "0%" }}
          >
            {t("navBar.title")}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              direction: direction === "ltr" ? "rtl" : "ltr",
            }}
          >
            <AccountCircleIcon />
            <Typography
              sx={{
                marginLeft: 1,
                paddingRight: direction === "ltr" ? "5px" : "0px",
              }}
            >
              {businessId}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { lg: adjustedDrawerWidth },
          flexShrink: { lg: 0 },
          backgroundColor: "#182250",
          color: "#fff",
        }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant={isMobileOrTablet ? "temporary" : "permanent"}
          open={isMobileOrTablet ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: adjustedDrawerWidth,
              backgroundColor: "#182250",
              color: "#fff",
            },
          }}
          anchor={direction === "rtl" ? "right" : "left"}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: {
            xs: "100%",
            sm: "100%",
            md: "100%",
            lg: `calc(100% - ${adjustedDrawerWidth}px)`,
            ...(isMobileOrTablet &&
              mobileOpen && {
                xs: `calc(100% - ${adjustedDrawerWidth}px)`,
                sm: `calc(100% - ${adjustedDrawerWidth}px)`,
                md: `calc(100% - ${adjustedDrawerWidth}px)`,
              }),
          },
        }}
      >
        <Box sx={{ ...theme.mixins.toolbar }} />
        <Outlet />
        <Box
          mt={2}
          dir="ltr"
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
          }}
        >
          <Typography
            onClick={() => changeLanguage("fr")}
            sx={{ color: "#000", cursor: "pointer" }}
            variant="caption"
          >
            Francais
          </Typography>
          <div> - </div>
          <Typography
            onClick={() => changeLanguage("ar")}
            sx={{ color: "#000", cursor: "pointer" }}
            variant="caption"
          >
            العربية
          </Typography>
        </Box>
      </Box>
      <CustomSnackbar />
    </Box>
  );
};

export default CustomLayout;
